import { articles as defaultArticles } from "../constants/articles";
import { supabaseService } from "./supabaseService";

export interface Article {
  slug: string;
  image: string;
  imageDetails: string;
  title?: string;
  titleKey: string;
  meta?: string;
  metaKey: string;
  contentTitleKey: string;
  contentTitle?: string;
  contentKey: string;
  tagsKey: string;
  article_key?: string;
  tags?: string[];

  // New bilingual content structure
  content?: {
    en: {
      title: string;
      meta: string;
      contentTitle: string;
      content: string;
      tags: string;
    };
    ru: {
      title: string;
      meta: string;
      contentTitle: string;
      content: string;
      tags: string;
    };
  };
}

class ArticleService {
  private readonly STORAGE_KEY = "admin_articles_data";
  private useSupabase = false;

  constructor() {
    // Check if Supabase is configured
    this.useSupabase = !!(
      process.env.REACT_APP_SUPABASE_URL &&
      process.env.REACT_APP_SUPABASE_ANON_KEY
    );
  }

  async getArticles(): Promise<Article[]> {
    let allArticles: Article[] = [];

    if (this.useSupabase) {
      try {
        const supabaseArticles = await supabaseService.getArticles();
        if (supabaseArticles && supabaseArticles.length > 0) {
          // Start with Supabase articles (they take priority)
          allArticles = [...supabaseArticles];

          // Add hardcoded articles only if they don't conflict with Supabase articles
          const existingSlugs = new Set(supabaseArticles.map((a) => a.slug));
          const hardcodedArticles = defaultArticles.filter(
            (article) => !existingSlugs.has(article.slug)
          );
          allArticles = [...allArticles, ...hardcodedArticles];
        } else {
          // If no Supabase articles, use hardcoded ones
          allArticles = [...defaultArticles];
        }
      } catch (error) {
        console.error("Error loading articles from Supabase:", error);
        // Fallback to hardcoded articles if Supabase fails
        allArticles = [...defaultArticles];
      }
    } else {
      // Fallback to localStorage when Supabase is not available
      try {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
          const parsedArticles = JSON.parse(stored);
          if (parsedArticles && parsedArticles.length > 0) {
            // Add localStorage articles, avoiding duplicates by slug
            const existingSlugs = new Set(allArticles.map((a) => a.slug));
            const newStoredArticles = parsedArticles.filter(
              (article: Article) => !existingSlugs.has(article.slug)
            );
            allArticles = [...allArticles, ...newStoredArticles];
          }
        }
      } catch (error) {
        console.error("Error loading articles from localStorage:", error);
      }
    }

    return allArticles;
  }

  saveArticles(articles: Article[]): void {
    if (this.useSupabase) {
      console.warn(
        "saveArticles called with Supabase enabled. Use individual CRUD methods instead."
      );
      return;
    }

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(articles));
    } catch (error) {
      console.error("Error saving articles:", error);
    }
  }

  async addArticle(article: Article): Promise<boolean> {
    if (this.useSupabase) {
      try {
        // Generate article_key for new articles
        const articleKey = await supabaseService.getNextArticleKey();

        // Create the article record
        const articleData = {
          slug: article.slug,
          article_key: articleKey,
          image_url: article.image,
          image_details_url: article.imageDetails,
          title_key: `articles.${articleKey}.title`,
          meta_key: `articles.${articleKey}.meta`,
          content_title_key: `articles.${articleKey}.contentTitle`,
          content_key: `articles.${articleKey}.content`,
          tags_key: `articles.${articleKey}.tags`,
        };

        const result = await supabaseService.createArticleWithTranslations(
          articleData
        );

        if (result && article.content) {
          // Save translations to i18n table
          await supabaseService.upsertTranslations(articleKey, {
            en: {
              title: article.content.en.title,
              meta: article.content.en.meta,
              contentTitle: article.content.en.contentTitle,
              content: article.content.en.content,
              tags: article.content.en.tags,
            },
            ru: {
              title: article.content.ru.title,
              meta: article.content.ru.meta,
              contentTitle: article.content.ru.contentTitle,
              content: article.content.ru.content,
              tags: article.content.ru.tags,
            },
          });
        }

        return !!result;
      } catch (error) {
        console.error("Error adding article to Supabase:", error);
        return false;
      }
    }

    // Fallback to localStorage
    const articles = await this.getArticles();
    const existingIndex = articles.findIndex((a) => a.slug === article.slug);

    if (existingIndex >= 0) {
      articles[existingIndex] = article;
    } else {
      articles.push(article);
    }

    this.saveArticles(articles);
    return true;
  }

  async updateArticle(article: Article): Promise<boolean> {
    if (this.useSupabase) {
      try {
        // Get the existing article to find its article_key
        const existingArticle = await supabaseService.getArticleBySlug(
          article.slug
        );

        if (!existingArticle) {
          console.error("Article not found for update:", article.slug);
          return false;
        }

        // Update the article record
        const articleData = {
          slug: article.slug,
          image_url: article.image,
          image_details_url: article.imageDetails,
        };

        const result = await supabaseService.updateArticleBasic(articleData);

        if (result && article.content) {
          // Update translations in i18n table
          const articleKey =
            existingArticle.article_key || `article_${article.slug}`;
          await supabaseService.upsertTranslations(articleKey, {
            en: {
              title: article.content.en.title,
              meta: article.content.en.meta,
              contentTitle: article.content.en.contentTitle,
              content: article.content.en.content,
              tags: article.content.en.tags,
            },
            ru: {
              title: article.content.ru.title,
              meta: article.content.ru.meta,
              contentTitle: article.content.ru.contentTitle,
              content: article.content.ru.content,
              tags: article.content.ru.tags,
            },
          });
        }

        return !!result;
      } catch (error) {
        console.error("Error updating article in Supabase:", error);
        return false;
      }
    }

    // Fallback to localStorage
    return await this.addArticle(article);
  }

  async deleteArticle(slug: string): Promise<boolean> {
    if (this.useSupabase) {
      return await supabaseService.deleteArticle(slug);
    }

    // Fallback to localStorage
    const articles = await this.getArticles();
    const filteredArticles = articles.filter((a) => a.slug !== slug);
    this.saveArticles(filteredArticles);
    return true;
  }

  // Fix article key conflicts
  async fixArticleKeyConflicts(): Promise<void> {
    if (!this.useSupabase) return;

    try {
      const articles = await this.getArticles();
      const supabaseArticles = articles.filter((a) => a.article_key);

      // Check for conflicts with hardcoded articles
      const hardcodedKeys = defaultArticles
        .map((article) => {
          const match = article.titleKey.match(/^articles\.([^.]+)\./);
          return match ? match[1] : null;
        })
        .filter(Boolean);

      for (const article of supabaseArticles) {
        if (
          article.article_key &&
          hardcodedKeys.includes(article.article_key)
        ) {
          console.log(`Fixing article key conflict for: ${article.slug}`);
          const newKey = await supabaseService.getNextArticleKey();
          await supabaseService.fixArticleKey(article.slug, newKey);
        }
      }
    } catch (error) {
      console.error("Error fixing article key conflicts:", error);
    }
  }

  async getArticleBySlug(slug: string): Promise<Article | null> {
    // First check hardcoded articles
    const hardcodedArticle = defaultArticles.find((a) => a.slug === slug);
    if (hardcodedArticle) {
      return hardcodedArticle;
    }

    // Then check Supabase if available
    if (this.useSupabase) {
      try {
        const supabaseArticle = await supabaseService.getArticleBySlug(slug);
        if (supabaseArticle) {
          return supabaseArticle;
        }
      } catch (error) {
        console.error("Error loading article from Supabase:", error);
      }
    }

    // Finally check localStorage
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsedArticles = JSON.parse(stored);
        const storedArticle = parsedArticles.find(
          (a: Article) => a.slug === slug
        );
        if (storedArticle) {
          return storedArticle;
        }
      }
    } catch (error) {
      console.error("Error loading article from localStorage:", error);
    }

    return null;
  }

  resetToDefault(): void {
    if (this.useSupabase) {
      console.warn(
        "resetToDefault called with Supabase enabled. This will only clear localStorage."
      );
    }
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Method to get default articles directly
  getDefaultArticles(): Article[] {
    return defaultArticles;
  }

  // Image upload methods
  async uploadImage(file: File, fileName: string): Promise<string | null> {
    if (this.useSupabase) {
      return await supabaseService.uploadImage(file, fileName);
    }

    // For localStorage mode, return a placeholder or handle differently
    console.warn("Image upload not supported in localStorage mode");
    return null;
  }

  async deleteImage(fileName: string): Promise<boolean> {
    if (this.useSupabase) {
      return await supabaseService.deleteImage(fileName);
    }

    console.warn("Image deletion not supported in localStorage mode");
    return false;
  }
}

export const articleService = new ArticleService();
